import { Component, type ErrorInfo, type ReactNode } from 'react';

type Props = { children: ReactNode };
type State = { hasError: boolean };

/**
 * Keeps a render error in one route from blanking the whole site. The message
 * is deliberately generic — stack details go to the console, not the page.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Unhandled render error', error, info.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="tone-aurora siv-panel mb-4 px-6 py-16 text-center">
        <h1 className="text-[clamp(1.75rem,4vw,2.75rem)]">
          Something on this page failed to draw.
        </h1>
        <p className="text-muted mx-auto mt-4 max-w-[46ch]">
          The rest of the site is unaffected. Reload to try again.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="border-line-strong bg-surface mt-8 inline-flex min-h-12 items-center rounded-full border px-6 font-semibold"
        >
          Reload the page
        </button>
      </div>
    );
  }
}
